import { useState } from 'react';
import type { CrmAudience } from '@cdf/api-client-react';
import { useAuth } from '@/components/auth/auth-provider';
import { useTenantEntity } from '@/components/access/tenant-entity-provider';
import { useWorkspace } from '@/components/workspace/workspace-provider';
import { NextActionsFeed } from './action-feed';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetTrigger } from '@/components/ui/sheet';

/** Same canonical feed and feedback on every surface, isolated by current actor/access. */
export function FollowUpSuggestions({ accountId, audience, limit = 5, disabled = false }: {
  accountId?: string; audience?: CrmAudience; limit?: number; disabled?: boolean;
}) {
  const auth = useAuth(), access = useTenantEntity(), workspace = useWorkspace();
  const selectedAudience = audience ?? (workspace.mode === 'all' ? undefined : workspace.mode);
  const boundary = JSON.stringify([auth.account?.stableId, access.selected, access.status,
    access.snapshot?.authorizationRevision, accountId, selectedAudience]);
  if (access.status !== 'ready' || !access.selected) return <p role="status" className="text-sm text-muted-foreground">Choose an authorized entity to see follow-up suggestions.</p>;
  return <NextActionsFeed key={boundary} boundary={boundary} suggestions disabled={disabled} accountId={accountId} audience={selectedAudience}
    limit={limit} showAudienceBadges scopeLabel={access.selected.entityName}
    emptyDescription="No open suggestions in this view. Dismissed and snoozed suggestions stay out of the list. Reload only checks existing work." />;
}

export function FollowUpDrawer({ disabled = false }: { disabled?: boolean }) {
  const [open, setOpen] = useState(false);
  return <Sheet open={open} onOpenChange={setOpen}>
    <SheetTrigger asChild><Button size="sm" variant="ghost" disabled={disabled}>Follow-up suggestions</Button></SheetTrigger>
    <SheetContent className="w-full overflow-y-auto sm:max-w-xl">
      <SheetHeader><SheetTitle>Follow-up suggestions</SheetTitle><SheetDescription>Choose existing work to inspect. Opening a review sends nothing and creates no records.</SheetDescription></SheetHeader>
      <div className="px-4 pb-4">{open && <FollowUpSuggestions disabled={disabled} />}</div>
    </SheetContent>
  </Sheet>;
}
